import React from "react";
import { Form, Input, Button, Card, Typography } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { icons } from "lucide-react";
import { defaultContactInfo } from "./defaults";
import { LucideIconPicker } from "./LucideIconPicker";
import type { SectionMeta } from "./types";

const { Text } = Typography;

export const meta: SectionMeta = {
  key: "contact_info",
  label: "Contact Information",
  icon: null,
  color: "#8b5cf6",
  desc: "Email, phone, address, and more",
};

export const defaultData = defaultContactInfo;

const DynamicIcon = ({ name, size }: { name: string; size?: number }) => {
  const IconComp =
    name && name in icons ? icons[name as keyof typeof icons] : null;
  return IconComp ? <IconComp size={size ?? 16} /> : null;
};

export const Preview: React.FC<{ data: any[] }> = ({ data }) => {
  if (data.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {data.map((item: any, i: number) => (
        <div key={i} className="flex items-center gap-2 text-sm px-2">
          <span className="text-base w-5 text-center shrink-0">
            {item.icon ? <DynamicIcon name={item.icon} size={16} /> : null}
          </span>
          <div className="min-w-0 flex-1">
            <span className="text-gray-800">{item.value}</span>
            <Text type="secondary" className="text-xs block truncate">
              {item.description}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
};

export const FormFields: React.FC = () => (
  <Form.List name="contact_info">
    {(fields, { add, remove }) => (
      <>
        {fields.map((field) => (
          <Card
            key={field.key}
            className="!mb-3"
            size="small"
            type="inner"
            extra={
              <Button
                type="text"
                danger
                size="small"
                onClick={() => remove(field.name)}
                icon={<DeleteOutlined />}
              />
            }
          >
            <div className="grid grid-cols-2 gap-3">
              <Form.Item
                {...field}
                name={[field.name, "icon"]}
                label="Icon"
                className="!mb-0"
                rules={[{ required: true, message: "Required" }]}
              >
                <LucideIconPicker />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, "title"]}
                label="Title"
                className="!mb-0"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="e.g. Email Us" />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, "value"]}
                label="Value"
                className="!mb-0"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="e.g. info@example.com" />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, "href"]}
                label="Link (href)"
                className="!mb-0"
              >
                <Input placeholder="e.g. mailto:info@example.com" />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, "description"]}
                label="Description"
                className="!mb-0 col-span-2"
              >
                <Input placeholder="e.g. We reply within 24 hours" />
              </Form.Item>
            </div>
          </Card>
        ))}
        <Button
          type="dashed"
          block
          onClick={() => add()}
          icon={<PlusOutlined />}
          className="!mb-1"
        >
          Add Contact Info
        </Button>
      </>
    )}
  </Form.List>
);
